import { z } from 'zod';
import { aanbodSchema } from './aanbod';
import { beoordelingSchema } from './beoordeling';
import { PAGINATIE_MAX_LIMIET, PAGINATIE_STANDAARD_LIMIET } from './common';
import { energielabelSchema } from './energielabel';

/** Sort orders the listings endpoint and the table column headers offer. */
export const LISTING_SORTERINGEN = [
  'nieuwste',
  'oudste',
  'prijs_op',
  'prijs_af',
  'prijs_per_m2_op',
  'prijs_per_m2_af',
  'oppervlakte_op',
  'oppervlakte_af',
  'kamers_op',
  'kamers_af',
  'adres_op',
  'adres_af',
  'postcode_op',
  'postcode_af',
  /* `_op`/`_af` is hier, net als hierboven, oplopend en aflopend - niet de
     kolom `beoordeeldOp`. Vandaar `gekeurd`: `beoordeeld_op` zou precies de
     naam van die kolom zijn en elke lezer één keer laten struikelen. */
  'gekeurd_op',
  'gekeurd_af',
] as const;
export const listingSorteringSchema = z.enum(LISTING_SORTERINGEN);
export type ListingSortering = z.infer<typeof listingSorteringSchema>;

export const LISTING_SORTERING_LABELS: Record<ListingSortering, string> = {
  nieuwste: 'Nieuwste eerst',
  oudste: 'Oudste eerst',
  prijs_op: 'Prijs laag - hoog',
  prijs_af: 'Prijs hoog - laag',
  prijs_per_m2_op: 'Prijs per m² laag - hoog',
  prijs_per_m2_af: 'Prijs per m² hoog - laag',
  oppervlakte_op: 'Oppervlakte klein - groot',
  oppervlakte_af: 'Oppervlakte groot - klein',
  kamers_op: 'Kamers weinig - veel',
  kamers_af: 'Kamers veel - weinig',
  adres_op: 'Adres A - Z',
  adres_af: 'Adres Z - A',
  postcode_op: 'Postcode oplopend',
  postcode_af: 'Postcode aflopend',
  gekeurd_op: 'Langst geleden gekeurd',
  gekeurd_af: 'Laatst gekeurd eerst',
};

export const LISTING_SORTERING_STANDAARD: ListingSortering = 'nieuwste';

/**
 * Accepts both `?energielabels=A&energielabels=B` and `?energielabels=A,B`, so a
 * filter survives a round trip through the URL without extra encoding.
 */
function lijstVan<T extends z.ZodType>(item: T) {
  return z
    .preprocess((waarde) => {
      if (waarde === undefined || waarde === null || waarde === '') return undefined;
      if (Array.isArray(waarde)) return waarde;
      if (typeof waarde === 'string') {
        return waarde
          .split(',')
          .map((deel) => deel.trim())
          .filter((deel) => deel.length > 0);
      }
      return waarde;
    }, z.array(item).min(1).optional())
    .optional();
}

/**
 * Grenzen van een `integer`-kolom in Postgres. Een filterwaarde daarbuiten kan
 * geen enkele woning raken, en zonder deze grens ging hij als parameter mee de
 * database in: die weigert de cast en het scherm kreeg 500 "er ging iets mis aan
 * onze kant" te zien terwijl de fout in de URL zat. Nu is het een 400 met de reden.
 */
export const INT4_MIN = -2_147_483_648;
export const INT4_MAX = 2_147_483_647;

/**
 * Een NUL-byte (0x00) mag niet in een Postgres-tekstwaarde staan. Zonder deze
 * controle ging `?zoekterm=%00` ongefilterd de query in, gaf Postgres
 * `invalid byte sequence for encoding "UTF8": 0x00`, en las het scherm "er ging
 * iets mis aan onze kant" terwijl de fout in de URL zat - dezelfde klasse als een
 * getal met een paar nullen te veel. Nu is het een 400 die zegt wat er mis is.
 * De andere stuurtekens gaan mee: ze zijn onzin in een zoekterm en verstoppen
 * zich in een gekopieerde regel uit een export.
 */
export function bevatStuurteken(waarde: string): boolean {
  for (let i = 0; i < waarde.length; i += 1) {
    const code = waarde.charCodeAt(i);
    // Tab, regelinvoer en regelterugloop mogen; de rest van C0 en DEL niet.
    if (code === 9 || code === 10 || code === 13) continue;
    if (code < 32 || code === 127) return true;
  }
  return false;
}

/** Dezelfde tekens eruit halen, voor een waarde die uit een bestand komt. */
export function striptStuurtekens(waarde: string): string {
  if (!bevatStuurteken(waarde)) return waarde;
  const uit: string[] = [];
  for (let i = 0; i < waarde.length; i += 1) {
    const code = waarde.charCodeAt(i);
    const mag = code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127);
    if (mag) uit.push(String.fromCharCode(code));
  }
  return uit.join('');
}

export function zonderStuurtekens<T extends z.ZodType<string>>(schema: T) {
  return schema.refine((waarde) => !bevatStuurteken(waarde), {
    message: 'Deze waarde bevat een stuurteken (zoals een NUL-byte) en kan niet gezocht worden.',
  });
}

const geheelGetal = z.coerce
  .number()
  .int()
  .min(INT4_MIN, `Deze waarde moet tussen ${INT4_MIN} en ${INT4_MAX} liggen.`)
  .max(INT4_MAX, `Deze waarde moet tussen ${INT4_MIN} en ${INT4_MAX} liggen.`)
  .optional();

/**
 * Every filter the woningen endpoints understand. All fields are optional and
 * ranges are inclusive on both ends; an empty filter returns everything.
 */
export const listingFilterSchema = z.object({
  prijsMin: geheelGetal,
  prijsMax: geheelGetal,

  m2Min: geheelGetal,
  m2Max: geheelGetal,

  energielabels: lijstVan(energielabelSchema),

  /**
   * Ons eigen oordeel. Hier zit het Kansjes-scherm op: dat is niets anders dan
   * `?beoordelingen=kansje` met een eigen naam in de rail. Als filter hoort het
   * hier en niet in het scherm, want de kaart, de tabel en de facettellingen
   * moeten het over dezelfde selectie eens blijven.
   */
  beoordelingen: lijstVan(beoordelingSchema),

  /**
   * Te koop, verkocht of uit het aanbod (MA-22). Weggelaten betekent te koop en
   * verkocht: wat ingetrokken of verlopen is staat niet in beeld tot het
   * terugkomt. `aanbod=verdwenen` laat juist die zien.
   *
   * Hetzelfde geldt voor het oordeel: zonder `beoordelingen` blijven afgekeurde
   * en definitief afgekeurde woningen uit beeld. Zie `bouwListingFilter`.
   */
  aanbod: lijstVan(aanbodSchema),

  /**
   * Woningen zonder energielabel - de groep die de facetten wél tellen (`null`)
   * maar die met een lijst van labels niet te kiezen is. In de URL mag het ook
   * als `?energielabels=geen`; de API vertaalt dat hiernaartoe.
   */
  zonderLabel: z.stringbool().optional(),

  kamersMin: geheelGetal,
  kamersMax: geheelGetal,

  plaatsen: lijstVan(zonderStuurtekens(z.string().min(1))),

  /**
   * Buurt als PC4-code, bijvoorbeeld `1073`. De bron levert geen buurtveld; de
   * eerste vier cijfers van de postcode zijn wat er wél in elke rij zit en in
   * Amsterdam een bruikbare indeling. Zie `buurt.ts` voor de namen.
   */
  buurten: lijstVan(z.string().regex(/^\d{4}$/, 'Een buurt is een postcode van vier cijfers.')),

  /**
   * Welke imports meetellen, op id. De datasettabel is de plek waar je ze
   * aanklikt: twee scrapes naast elkaar zijn pas te vergelijken als je ze los
   * kunt aanzetten, en de kaart hoort dan precies dezelfde woningen te tonen als
   * de tabel eronder. Een woning hoort bij de laatste import die haar aanraakte,
   * dus dit is altijd een keuze uit wat er nu staat.
   */
  datasets: lijstVan(z.uuid('Een dataset kies je op zijn id; dit is er geen.')),

  /** Free-text search over address, postcode and city. */
  zoekterm: zonderStuurtekens(z.string().trim().min(1).max(120)).optional(),

  /**
   * Woningen zonder coordinaat - de rijen die wel in de tabel staan maar niet op
   * de kaart. De brief vraagt om ze zichtbaar te maken; zonder filter zijn ze
   * tussen duizend andere rijen niet te vinden. `false` keert het om: alleen wat
   * wel een punt heeft.
   */
  zonderCoordinaat: z.stringbool().optional(),

  /** Map viewport as west,south,east,north - limits results to what is visible. */
  bbox: z
    .preprocess(
      (waarde) => {
        if (typeof waarde === 'string') return waarde.split(',').map(Number);
        return waarde;
      },
      z.tuple([z.number(), z.number(), z.number(), z.number()]),
    )
    .optional(),

  sortering: listingSorteringSchema.default(LISTING_SORTERING_STANDAARD),

  limiet: z.coerce
    .number()
    .int()
    .min(1, `De paginagrootte ligt tussen 1 en ${PAGINATIE_MAX_LIMIET}.`)
    .max(PAGINATIE_MAX_LIMIET, `De paginagrootte ligt tussen 1 en ${PAGINATIE_MAX_LIMIET}.`)
    .default(PAGINATIE_STANDAARD_LIMIET),
  cursor: z.string().min(1).optional(),
});

export type ListingFilter = z.infer<typeof listingFilterSchema>;
/** The filter as it arrives from a query string, before parsing and defaults. */
export type ListingFilterInvoer = z.input<typeof listingFilterSchema>;

/** The filter without pagination: what the map, the facets and the GeoJSON use. */
export const listingFilterCriteriaSchema = listingFilterSchema.omit({
  limiet: true,
  cursor: true,
});
export type ListingFilterCriteria = z.infer<typeof listingFilterCriteriaSchema>;

/** True when the filter narrows nothing down - lets a caller skip a COUNT. */
export function isLegeFilter(filter: Partial<ListingFilterCriteria>): boolean {
  const { sortering: _sortering, ...rest } = filter;
  return Object.values(rest).every((waarde) => waarde === undefined);
}
